import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, ListChecks, ClipboardCheck, Database, Workflow, Truck, FileText, FolderLock,
  AlertTriangle, Wrench, FileBarChart, BookOpen, Settings, ShieldCheck, Sparkles, LogOut, Menu, Clock, UserCheck, BadgeCheck, Siren, ScanSearch, ShieldHalf,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Copilot } from "@/components/app/Copilot";
import { LanguageSelector } from "@/components/app/LanguageSelector";
import { can } from "@/lib/api";

const NAV = [
  ["Overview", [["/", "Dashboard", LayoutDashboard], ["/organisation", "Organisation", Building2], ["/applicability", "Applicability", ListChecks], ["/assessments", "Assessments", ClipboardCheck], ["/governance", "Security & Governance", ShieldHalf]]],
  ["Records", [["/inventory", "Data Inventory", Database], ["/processing", "Processing Inventory", Workflow], ["/processing-register", "Processing Register (legacy)", Workflow], ["/vendors", "Vendors / Processors", Truck], ["/consents", "Consent Register", BadgeCheck], ["/rights-requests", "Rights Requests", UserCheck], ["/breaches", "Breach Register", Siren]]],
  ["Discovery", [["/data-sources", "Data Sources", ScanSearch], ["/data-protection", "Data Protection", ShieldHalf]]],
  ["Evidence", [["/policies", "Policies & Documents", FileText], ["/evidence", "Evidence", FolderLock]]],
  ["Risk", [["/findings", "Findings", AlertTriangle], ["/remediation", "Remediation", Wrench], ["/reports", "Reports", FileBarChart]]],
  ["Reference", [["/regulatory", "Regulatory Library", BookOpen], ["/settings", "Settings", Settings]]],
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "");

function SideNav({ onNavigate }) {
  return (
    <nav className="flex h-full flex-col bg-slate-950 text-slate-300">
      <div className="flex items-center gap-2.5 px-5 py-5 border-b border-slate-800">
        <div className="grid h-8 w-8 place-items-center rounded-md bg-blue-600"><ShieldCheck className="h-4 w-4 text-white" /></div>
        <div>
          <div className="font-display text-sm font-bold text-white leading-tight">DPDP Compliance Hub</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Assessment · Evidence · Risk</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-3">
        {NAV.map(([group, items]) => (
          <div key={group} className="mb-3">
            <div className="px-5 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-600">{group}</div>
            {items.map(([to, label, Icon]) => (
              <NavLink key={to} to={to} end={to === "/"} onClick={onNavigate} data-testid={`nav-${slug(label)}`}
                className={({ isActive }) => `flex items-center gap-2.5 border-l-4 px-4 py-2 text-[13px] transition-colors ${isActive ? "border-blue-500 bg-slate-900 text-white" : "border-transparent hover:bg-slate-900/60 hover:text-white"}`}>
                <Icon className="h-4 w-4" />{label}
              </NavLink>
            ))}
          </div>
        ))}
      </div>
      <div className="border-t border-slate-800 px-5 py-3 text-[10px] leading-relaxed text-slate-500">
        Assessment platform — not a substitute for legal advice or a DPO.
      </div>
    </nav>
  );
}

function Countdown() {
  const days = Math.ceil((new Date("2027-05-13") - new Date()) / 86400000);
  return (
    <div data-testid="header-countdown" className="hidden lg:flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-xs text-purple-800">
      <Clock className="h-3.5 w-3.5" />
      {days > 0 ? <span><b>{days} days</b> until operational DPDP obligations (13 May 2027)</span> : <span>Operational DPDP obligations in force</span>}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [mobile, setMobile] = useState(false);
  const [copilot, setCopilot] = useState(false);
  const loc = useLocation();
  return (
    <div className="flex h-screen overflow-hidden">
      <aside className="hidden w-64 shrink-0 md:block"><SideNav /></aside>
      <Sheet open={mobile} onOpenChange={setMobile}>
        <SheetContent side="left" className="w-64 p-0 border-0"><SideNav onNavigate={() => setMobile(false)} /></SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur-md md:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobile(true)} data-testid="mobile-nav-toggle"><Menu className="h-5 w-5" /></Button>
            <div className="min-w-0">
              <div data-testid="header-org-name" data-no-translate className="truncate text-sm font-semibold text-slate-900">{user?.tenant?.name}</div>
              <div className="text-[11px] text-slate-500">{user?.tenant?.plan ? `${user.tenant.plan[0].toUpperCase()}${user.tenant.plan.slice(1)} plan` : ""}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Countdown />
            <LanguageSelector />
            {can(user, "copilot") && (
              <Button size="sm" onClick={() => setCopilot(true)} data-testid="open-copilot-button" className="bg-blue-600 hover:bg-blue-700">
                <Sparkles className="mr-1.5 h-4 w-4" />Copilot
              </Button>
            )}
            <div className="hidden sm:block text-right">
              <div data-testid="header-user-name" data-no-translate className="text-xs font-medium text-slate-800">{user?.name}</div>
              <div data-testid="header-user-role" className="text-[11px] text-slate-500">{user?.role_label}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button" title="Sign out"><LogOut className="h-4 w-4" /></Button>
          </div>
        </header>
        <main key={loc.pathname} className="flex-1 overflow-y-auto px-4 py-6 md:px-8">
          {user?.tenant && !user.tenant.consent_ok && <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="consent-required-banner">
            Organisation data-processing authorisation is pending. Uploads, AI analysis, data-source scans and Copilot are paused until an Organisation Admin accepts the disclosures{user.role === "admin" && <> — <a href="/consent" className="font-semibold underline">review now</a></>}.</div>}
          <Outlet /></main>
      </div>
      <Copilot open={copilot} onOpenChange={setCopilot} />
    </div>
  );
}
